import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { useMemo } from 'react';

import type { ElektraConditionContext } from '@/config/conditions';

import { ConditionProvider } from '@/config/conditions';

// ============================================================================
// Types
// ============================================================================

type ElektraConditionProviderProps = {
    children: React.ReactNode;
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Builds the ElektraConditionContext from various data sources.
 * This hook combines auth, router, project data, and inspection data into a unified context.
 *
 * @returns The complete condition context
 */
export function useElektraConditionContext(): ElektraConditionContext {
    const auth = useAuthentication();
    const location = useLocation();
    const { context, hasInspectionKind } = useElectricalProject();

    // Fetch inspection data to get enableExtendedRiso
    const { data: inspectionData } = useSuspenseQuery(
        elektraAppQueryOptions.project.inspection.information.inspectionsEditOptions({
            electricalProjectVersionId: context.electricalProjectVersionId,
        }),
    );

    // Fetch devices to check for ResidualCurrentCircuitBreakerDevice (legacy support)
    const { data: devicesData } = useSuspenseQuery(
        elektraAppQueryOptions.project.distributionBoardDeviceListOptions({
            electricalProjectVersionId: context.electricalProjectVersionId,
        }),
    );

    const enableExtendedRiso = inspectionData?.Inspection?.EnableExtendedRisoOnDistributionBoardDevices ?? false;

    const hasResidualCurrentCircuitBreakerDevices = useMemo(() => {
        const rccbDevices = devicesData?.Devices?.filter(device => device.DeviceKind === 'ResidualCurrentCircuitBreakerDevice' && !device.IsDeleted) ?? [];

        return rccbDevices.length > 0;
    }, [devicesData?.Devices]);

    return useMemo(() => ({
        // Base context fields
        currentUser: auth.currentUser,
        isAuthenticated: auth.isAuthenticated,
        currentPath: location.pathname,

        // Elektra-specific fields
        hasInspectionKind,
        enabledElectricalProjectPriceQuote: context?.enabledElectricalProjectPriceQuote ?? false,
        enabledElectricalProjectPlanning: context?.enabledElectricalProjectPlanning ?? false,
        enableExtendedRiso,
        hasResidualCurrentCircuitBreakerDevices,
        // Enable impedance measurements page to show Zi/Zs fields on dedicated page
        enableImpedanceMeasurementsPage: false,
    }), [
        auth.currentUser,
        auth.isAuthenticated,
        location.pathname,
        context?.enabledElectricalProjectPriceQuote,
        context?.enabledElectricalProjectPlanning,
        hasInspectionKind,
        enableExtendedRiso,
        hasResidualCurrentCircuitBreakerDevices,
    ]);
}

// ============================================================================
// Provider Component
// ============================================================================

/**
 * Provider component that makes the unified condition context available.
 * Wrap this around components that need access to conditions.
 *
 * This provider combines:
 * - Authentication state (currentUser, isAuthenticated)
 * - Router state (currentPath)
 * - Electrical project state (feature flags, inspection kinds)
 * - Inspection data (enableExtendedRiso)
 *
 * @example
 * // In project layout route:
 * <ElektraConditionProvider>
 *   <NavigationProvider value={navigationContext}>
 *     <Outlet />
 *   </NavigationProvider>
 * </ElektraConditionProvider>
 */
export function ElektraConditionProvider({
    children,
}: ElektraConditionProviderProps) {
    const conditionContext = useElektraConditionContext();

    return (
        <ConditionProvider value={conditionContext}>
            {children}
        </ConditionProvider>
    );
}
